import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useRef, useState } from 'react';
import { IoStar, IoStarHalf } from 'react-icons/io5';

export type RatingProps = {
  rating: number;
  ratingCount?: number;
  hideTextRating?: boolean;
  onRatingChange?: (rating: number) => void;
  className?: {
    main?: string;
    wrapper?: string;
    starIcon?: string;
    starWrapper?: string;
    starDisabled?: string;
  };
};

export function ProductRating({
  rating,
  ratingCount,
  className,
  onRatingChange,
  hideTextRating,
}: RatingProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoverRating, setHoverRating] = useState<number | null>(null);

  // An explicit zero count is an empty aggregate, not a five-star rating.
  // Individual reviews and the interactive review form may omit the count.
  const ratingProp = !onRatingChange && ratingCount === 0 ? 0 : rating ?? 0;

  const stars: any[] = [];

  for (let i = 0; i < 5; i++) {
    const rating = hoverRating ?? ratingProp;
    if (i + 1 <= rating) {
      stars.push(
        <div
          className={cn(
            'relative h-6 w-6',
            'ea-product-detail-rating__star-wrapper',
            !!onRatingChange && 'cursor-pointer',
            className?.starWrapper,
          )}
          key={i}
        >
          <IoStar
            className={cn(
              'text-rating h-6 w-6',
              'ea-product-detail-rating__star-icon',
              !!hoverRating && 'text-rating/80',
              className?.starIcon,
            )}
            fill="currentColor"
          />
        </div>,
      );
    } else if (i === Math.floor(rating) && rating % 1 !== 0) {
      stars.push(
        <div
          className={cn(
            'relative h-6 w-6',
            'ea-product-detail-rating__half-star-wrapper',
            !!onRatingChange && 'cursor-pointer',
            className?.starWrapper,
          )}
          key={i}
        >
          <IoStar
            className={cn(
              'absolute left-0 top-0 h-6 w-6 text-white',
              'ea-product-detail-rating__star-icon',
              className?.starIcon,
              className?.starDisabled,
            )}
          />
          <IoStarHalf
            className={cn(
              'text-rating absolute left-0 top-0 h-6 w-6',
              'ea-product-detail-rating__half-star-icon',
              !!hoverRating && 'text-rating/80',
              className?.starIcon,
            )}
            fill="currentColor"
          />
        </div>,
      );
    } else {
      stars.push(
        <div
          className={cn(
            'relative h-6 w-6',
            'ea-product-detail-rating__star--disabled-wrapper',
            !!onRatingChange && 'cursor-pointer',
            className?.starWrapper,
          )}
          key={i}
        >
          <IoStar
            className={cn(
              'h-6 w-6 text-white',
              'ea-product-detail-rating__star ea-product-detail-rating__star--disabled',
              className?.starIcon,
              className?.starDisabled,
            )}
          />
        </div>,
      );
    }
  }

  const getRatingFromEvent = (e: MouseEvent) => {
    if (!containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - containerRect.left;

    const value = Math.max(0, Math.min(x / containerRect.width, 1));
    return Math.ceil(value * 5);
  };

  useEffect(() => {
    if (!onRatingChange) return;

    const container = containerRef.current;

    const onMouseMove = (e: MouseEvent) => {
      const newRating = getRatingFromEvent(e);
      if (!newRating) return;

      setHoverRating(newRating);
    };

    const onMouseLeave = () => {
      setHoverRating(null);
    };

    container?.addEventListener('mousemove', onMouseMove);
    container?.addEventListener('mouseleave', onMouseLeave);

    return () => {
      container?.removeEventListener('mousemove', onMouseMove);
      container?.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [onRatingChange]);

  return (
    <div className={cn('flex', className?.main)}>
      <div
        className={cn(
          'flex items-center space-x-1',
          'ea-product-detail-rating',
          className?.wrapper,
        )}
        ref={containerRef}
        onClick={e => {
          if (!onRatingChange) return;
          /** Don't use hover rating because if we are on mobile, we don't have to have hover prior clicking */
          const newRating = getRatingFromEvent(e as unknown as MouseEvent);
          if (!newRating) return;
          onRatingChange(newRating);
        }}
      >
        {stars}
      </div>
      {!hideTextRating && (
        <div
          className={cn(
            'flex items-center text-sm font-light text-black',
            onRatingChange && 'ml-2 font-bold',
          )}
        >
          {!onRatingChange
            ? ratingCount != null
              ? `(${parseInt(ratingCount.toString()).toFixed(0)})`
              : `${parseFloat(ratingProp.toString()).toFixed(1)}/5`
            : parseFloat(ratingProp.toString()).toFixed(1)}
        </div>
      )}
    </div>
  );
}
